import { once } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { User } from '@/features/iam/users/User'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import { generateAvatarFallback } from '@/utils/image-util'
import { type TimeRange } from '@/infra/time-range'
import { toStream, asAtom } from '@/utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { countdown } from '@/utils/rx-util'
import { of } from 'rxjs'
import { type InventoryItemType } from '@/features/inventory/InventoryItem'

/**
 * The routing mode used for the opportunity.
 * - `FirstComeFirstServed`: the first pro to claim the job gets the job.
 * - `Dispatch`: pros apply for jobs and are selected based upon a heuristic.
 */
export type RoutingMode = 'FirstComeFirstServed' | 'Dispatch'

export type OpportunityProps = ModelProps<Opportunity>

/**
 * Opportunity model.
 */
export class Opportunity extends Model<Opportunity> {
  /**
   * An observable countdown. Ticks every second in sync with clock time until
   * the scheduled dispatch time is reached. `null` indicates that no selection
   * is currently scheduled or the selection date has been reached.
   */
  private readonly _timeUntilSelection$ = toStream(
    () => this.dateScheduledDispatch,
    true,
  ).pipe(switchMap((date) => (date ? countdown(date) : of(null))))

  /**
   * MobX-representation the time until selection stream.
   */
  private readonly _timeUntilSelection = asAtom(this._timeUntilSelection$, null)

  @observable private accessor _isPickingUp = false
  @observable private accessor _isCandidate = false

  id = Id.none
  @observable accessor workspace = Workspace.none
  @observable accessor name = ''
  @observable accessor description = ''
  @observable accessor clientUser: User | null = null
  @observable accessor legend: OpportunityLegendDescriptor | undefined =
    undefined
  @observable accessor scopeItems: OpportunityScopeItem[] = []
  @observable accessor infoLines: OpportunityInfoLine[] = []
  @observable accessor dateDeadline: Date | null = null
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor dateTransmitted = unixEpoch()
  @observable accessor providerCutAmount = 0
  @observable accessor consultation: OpportunityConsultation | null = null
  @observable accessor dateScheduledDispatch: Date | null = null
  @observable accessor routingMode: RoutingMode = 'FirstComeFirstServed'

  /**
   * The client on the opportunity.
   */
  @computed
  get client() {
    return this.clientUser
      ? new OpportunityClient(this.workspace, this.clientUser)
      : OpportunityClient.none
  }

  /**
   * Whether to show the deadline date according to workspace classification.
   */
  @computed
  get shouldShowDeadline() {
    const validWorkspaces = this.workspace.classification

    // on B2E workspaces always show the deadline
    return validWorkspaces === 'B2E'
  }

  /**
   * Tracks whether the current user is trying to pick up this opportunity.
   * Used to ensure we don't trigger swooping UX for the current user if
   * events come in before the pickup call succeeds.
   */
  @computed get isPickingUp() {
    return this._isPickingUp
  }

  /**
   * Indicates whether the user is a candidate for the job.
   */
  @computed
  get isCandidate() {
    return this._isCandidate
  }

  /**
   * Empty instance.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * Indicates whether the opportunity can be applied for.
   */
  @computed
  get isDispatchOpportunity() {
    return this.routingMode === 'Dispatch'
  }

  /**
   * The number of seconds until selection for the Opportunity. Null if
   * not selecting.
   */
  @computed
  get timeUntilSelection() {
    return this._timeUntilSelection()
  }

  /**
   * Whether the opportunity contains bookkeeping.
   */
  @computed
  get hasBookkeeping() {
    return this.scopeItems.some(
      (item) => item.inventoryItemType === 'Bookkeeping',
    )
  }

  /**
   * Marks this opportunity as being picked up by the current user.
   *
   * @returns A function to call that undoes it.
   */
  @action.bound
  markAsPickingUp() {
    this._isPickingUp = true
    return action(() => {
      this._isPickingUp = false
    })
  }

  /**
   * Marks the user as a candidate or not for the job.
   */
  @action.bound
  markUserAsCandidate(isCandidate: boolean) {
    this._isCandidate = isCandidate
  }
}

const lazyNone = once(() => new Opportunity())

/**
 * Opportunity legend descriptor.
 */
export interface OpportunityLegendDescriptor {
  id: Id
  version: number
  name: string
  priceUnit: string | undefined
}

/**
 * Opportunity info line.
 */
export interface OpportunityInfoLine {
  title: string
  content: string
}

/**
 * Opportunity scope item.
 */
export interface OpportunityScopeItem {
  name: string
  quantity: number
  inventoryItemType: InventoryItemType
}

/**
 * An Opportunity-representation of a Consultation.
 */
export interface OpportunityConsultation {
  /**
   * The ID of the Consultation.
   */
  consultationId: Id

  /**
   * The bookable time slots, the client's availability split into slots of
   * the requested length.
   */
  availability: TimeRange[]

  /**
   * The requested length for the Consultation, in minutes.
   */
  requestedLength: number
}

/**
 * The client on the opportunity.
 */
export class OpportunityClient {
  constructor(
    private readonly _workspace: Workspace,
    private readonly _user: User,
  ) {}

  /**
   * Empty instance.
   */
  static get none() {
    return lazyNoneClient()
  }

  /**
   * Whether we are allowed to view this info.
   */
  @computed
  get canViewInfo() {
    return !this._workspace.config.jobs.anonymousOpportunities
  }

  /**
   * The client's display name.
   */
  @computed
  get displayName() {
    if (!this.canViewInfo) {
      return ''
    }

    return this._user.displayName
  }

  /**
   * The client's avatar.
   */
  @computed
  get avatar() {
    if (!this.canViewInfo) {
      return ''
    }

    return this._user.avatar || this.avatarFallback
  }

  /**
   * The client's avatar fallback.
   * Since we only reference a `User`, we need to use the Opportunity's
   * workspace to get the fallback color.
   */
  @computed
  get avatarFallback() {
    if (!this.canViewInfo) {
      return ''
    }

    return generateAvatarFallback(
      this._user.displayName,
      this._workspace.branding.colors.primary,
    )
  }

  /**
   * User information for the client.
   */
  @computed
  get user() {
    return this._user
  }
}

const lazyNoneClient = once(
  () => new OpportunityClient(Workspace.none, User.none),
)
